<template>
  <div>
    <!-- TODO: remove after initial launch    -->
    <button style="display: none" @click="handleTest" @keydown="onKeydown">Test is in app</button>
  </div>
</template>
<script setup lang="ts">
  import * as Sentry from '@sentry/vue';
  import { nextTick, onMounted } from 'vue';
  import { useNhsLoginSso } from '../composables/useNhsLoginSso';
  import { useRoute, useRouter } from 'vue-router';
  import { useSessionStore } from '@/stores/useSessionStore';
  import { useProvidedSessionInactivity } from '../composables/useProvidedSessionInactivity';
  import { useErrorStore } from '@/stores/useErrorStore';
  import { RequestTypeBroad } from '@/types';
  import { useNhsApp } from '../nhsapp/useNhsApp';
  import { useNhsAppStore } from '@/stores/useNhsAppStore';

  import { storeToRefs } from 'pinia';
  const router = useRouter();
  const route = useRoute();
  const session = useSessionStore();
  const inactivity = useProvidedSessionInactivity();
  const errorStore = useErrorStore();
  const nhsAppStore = useNhsAppStore();

  const { isOpenInNhsApp, isNhsWebApp, appPlatform } = storeToRefs(nhsAppStore);

  // NB. loads SDK and sets nhsAppStore values
  const nhsapp = useNhsApp({ autoLoad: true });
  function onKeydown(e: KeyboardEvent) {
    // Allow debugging of NHS SDK (if we hide the button)
    if (e.key.toLowerCase() === 't') {
      e.preventDefault();
      handleTest();
    }
  }

  function handleTest() {
    nhsapp.testIsInApp();
  }

  async function navigateForRequestType(requestType: RequestTypeBroad) {
    if (requestType === 'medical-request') {
      await router.push('/medical-request/');
      return;
    }
    if (requestType === 'admin-request') {
      await router.push('/admin-request/');
      return;
    }
  }

  const nhsLoginSso = useNhsLoginSso({
    onNhsError: async ({ error }) => {
      const errorForStore = [{ code: error?.trim() }];
      // NB. needed so we show correct UI on onNhsError
      nhsAppStore.setIsNhsWebApp(true);

      errorStore.setErrors(errorForStore);

      errorStore.setIsNhsAppError(true);
      errorStore.setIsNhsError(true);
    },
  });

  onMounted(async () => {
    const requestType = route.params.section as RequestTypeBroad;

    const assertedLoginIdentity = route.query?.assertedLoginIdentity;
    const fromNhs = route.query?.from;
    if (fromNhs === 'nhsApp') {
      nhsAppStore.setIsNhsWebApp(true);
      await nextTick();
    }
    if (await nhsLoginSso.checkForNhsErrorParams()) return;
    nhsAppStore.setIsNhsWebApp(true);

    // NHSApp round 01
    // ?assertedLoginIdentity=asd
    // &from=nhsApp
    if (assertedLoginIdentity) {
      // uses Medicus Id
      await nhsLoginSso.verifySsoTokenWithNhsLogin();
      return;
    }

    // NHSApp round 02
    // ?code=asdldsjflkjfd
    // &state=asdkjfjkdsh
    const authenticated = await nhsLoginSso.authenticateLoggedInUser();

    // We know we are authenticated & in nhs app world here
    let appContext = 'web';
    if (isOpenInNhsApp.value) {
      appContext = 'nhsapp';
    } else if (isNhsWebApp.value) {
      appContext = 'nhsapp-web';
    }

    // NB. lets tell sentry what app and app platform we are in
    Sentry.setTag('app', appContext);
    Sentry.setTag('appPlatform', appPlatform.value ?? 'unknown');

    if (!authenticated) return;

    session.startSession(Date.now());

    // Session timer already started but overwrite sessionEndPath
    inactivity.start({ sessionEndedPath: 'page/you-have-been-logged-out' });

    await navigateForRequestType(requestType);
  });
</script>
